import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Play, Bot, IdCard, Building2, HeartHandshake, UserRound,
  FilePen, Video, Network, ChartPie, SearchCheck, WandSparkles, Waves, Brain,
  Dumbbell, Check, UsersRound,
} from 'lucide-react';
import MarketingLayout, { NumuvLogo, WhatsAppIcon, useWaLink } from '@/components/marketing/MarketingLayout';
import Seo, { SITE } from '@/components/shared/Seo';
import { Toaster } from '@/components/ui/sonner';

/* -------- Hero convergence — split into desktop (SVG lines) + mobile (stacked) -------- */

function DesktopConvergence() {
  // Y-shape layout: 3 role cards on top, core below.
  // ViewBox is 1200x620 so there is real vertical breathing room between cards and orb.
  return (
    <div className="hidden lg:block relative mx-auto mt-16 w-full max-w-[1200px] aspect-[1200/620]" data-testid="hero-convergence-desktop">
      <svg viewBox="0 0 1200 620" className="absolute inset-0 h-full w-full" fill="none" preserveAspectRatio="xMidYMid meet">
        <defs>
          {/* userSpaceOnUse ensures the gradient renders even on paths whose bounding box has zero width/height. */}
          <linearGradient id="lg1" x1="306" y1="128" x2="600" y2="430" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#14B8A6" /><stop offset="1" stopColor="#6D5EF7" /></linearGradient>
          <linearGradient id="lg2" x1="600" y1="192" x2="600" y2="430" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#8B5CF6" /><stop offset="1" stopColor="#6D5EF7" /></linearGradient>
          <linearGradient id="lg3" x1="894" y1="128" x2="600" y2="430" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#4F7BF7" /><stop offset="1" stopColor="#6D5EF7" /></linearGradient>
        </defs>
        {/* Applicants: exits the RIGHT edge (306,128). Staffing: exits the BOTTOM center (600,210). Hiring: exits the LEFT edge (894,128). All converge on the core top (600,430), dipping below the middle card. */}
        <path className="baseline" d="M306,128 C420,160 560,290 600,430" strokeWidth="1.5" />
        <path className="baseline" d="M600,192 C598,280 602,370 600,430" strokeWidth="1.5" />
        <path className="baseline" d="M894,128 C780,160 640,290 600,430" strokeWidth="1.5" />
        {/* Flowing gradients — same paths used for animateMotion. The middle path uses a tiny S-curve so the gradient bounding box is non-degenerate. */}
        <path id="p1" d="M306,128 C420,160 560,290 600,430" stroke="url(#lg1)" strokeWidth="3" opacity=".9" strokeLinecap="round" />
        <path id="p2" d="M600,192 C598,280 602,370 600,430" stroke="url(#lg2)" strokeWidth="3" opacity=".9" strokeLinecap="round" />
        <path id="p3" d="M894,128 C780,160 640,290 600,430" stroke="url(#lg3)" strokeWidth="3" opacity=".9" strokeLinecap="round" />
        <circle r="6" fill="#14B8A6"><animateMotion dur="3.4s" repeatCount="indefinite"><mpath href="#p1" /></animateMotion></circle>
        <circle r="4" fill="#6D5EF7"><animateMotion dur="3.4s" begin="1.1s" repeatCount="indefinite"><mpath href="#p1" /></animateMotion></circle>
        <circle r="6" fill="#8B5CF6"><animateMotion dur="2.8s" repeatCount="indefinite"><mpath href="#p2" /></animateMotion></circle>
        <circle r="4" fill="#6D5EF7"><animateMotion dur="2.8s" begin="1s" repeatCount="indefinite"><mpath href="#p2" /></animateMotion></circle>
        <circle r="6" fill="#4F7BF7"><animateMotion dur="3.6s" repeatCount="indefinite"><mpath href="#p3" /></animateMotion></circle>
        <circle r="4" fill="#6D5EF7"><animateMotion dur="3.6s" begin="1.3s" repeatCount="indefinite"><mpath href="#p3" /></animateMotion></circle>
      </svg>

      {/* Cards — wrappers are centered via translate; the float animation lives on an INNER div so it doesn't override the centering transform. */}
      <div className="absolute" style={{ left: `${(200 / 1200) * 100}%`, top: `${(128 / 620) * 100}%`, transform: 'translate(-50%,-50%)' }}>
        <div className="float">
          <NodeCard testId="node-applicants" icon={UserRound} color="#12B6A6" title="Applicants"
            desc="Profile → auto-match → AI interview → offer" metricLabel="Match" metricValue="94%" />
        </div>
      </div>
      <div className="absolute" style={{ left: '50%', top: `${(128 / 620) * 100}%`, transform: 'translate(-50%,-50%)' }}>
        <div className="float-slow">
          <NodeCard testId="node-staffing" icon={HeartHandshake} color="#8B5CF6" title="Staffing Orgs"
            desc="Onboard talent → manage partners → build connections" metricLabel="Active pools" metricValue="128" />
        </div>
      </div>
      <div className="absolute" style={{ left: `${(1000 / 1200) * 100}%`, top: `${(128 / 620) * 100}%`, transform: 'translate(-50%,-50%)' }}>
        <div className="float">
          <NodeCard testId="node-hiring" icon={Building2} color="#4F7BF7" title="Hiring Orgs"
            desc="Post roles → AI questions → track → hire" metricLabel="Time saved" metricValue="73%" />
        </div>
      </div>
      {/* Core — center at y=505 (505/620 = 81.4%). Core diameter 140 → top edge at y=435, matches path endpoint (y=430). No overlap with cards. */}
      <div className="absolute" style={{ left: '50%', top: `${(505 / 620) * 100}%`, transform: 'translate(-50%,-50%)' }}>
        <Core />
      </div>
    </div>
  );
}

function MobileConvergence() {
  // Y-shape stack: 3 cards row on top, orb below.
  return (
    <div className="lg:hidden relative mx-auto mt-10 w-full max-w-[520px] px-2" data-testid="hero-convergence-mobile">
      <div className="grid grid-cols-3 gap-2 items-start">
        <NodeCard compact testId="node-applicants" icon={UserRound} color="#12B6A6" title="Applicants"
          desc="Profile → match → interview" metricLabel="Match" metricValue="94%" />
        <NodeCard compact testId="node-staffing" icon={HeartHandshake} color="#8B5CF6" title="Staffing"
          desc="Onboard, manage, connect" metricLabel="Pools" metricValue="128" />
        <NodeCard compact testId="node-hiring" icon={Building2} color="#4F7BF7" title="Hiring"
          desc="Post → screen → hire" metricLabel="Saved" metricValue="73%" />
      </div>
      {/* Trio of vertical connector lines fanning into the core */}
      <div className="relative h-14 mt-2">
        <svg viewBox="0 0 300 60" className="absolute inset-0 w-full h-full" preserveAspectRatio="none" fill="none">
          <defs>
            <linearGradient id="mlg1" x1="50" y1="0" x2="150" y2="58" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#14B8A6" /><stop offset="1" stopColor="#6D5EF7" /></linearGradient>
            <linearGradient id="mlg2" x1="150" y1="0" x2="150" y2="58" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#8B5CF6" /><stop offset="1" stopColor="#6D5EF7" /></linearGradient>
            <linearGradient id="mlg3" x1="250" y1="0" x2="150" y2="58" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#4F7BF7" /><stop offset="1" stopColor="#6D5EF7" /></linearGradient>
          </defs>
          <path d="M50,0 Q50,30 150,58" stroke="url(#mlg1)" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M150,0 C148,20 152,40 150,58" stroke="url(#mlg2)" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M250,0 Q250,30 150,58" stroke="url(#mlg3)" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </div>
      <div className="flex justify-center">
        <Core small />
      </div>
    </div>
  );
}

function NodeCard({ testId, icon: Icon, color, title, desc, metricLabel, metricValue, compact = false, align = 'left' }) {
  return (
    <div
      data-testid={testId}
      className={`card card-hover soft rounded-[16px] md:rounded-[20px] ${compact ? 'p-2.5' : 'p-3 md:p-4'} text-left ${compact ? 'w-full' : 'w-[190px] md:w-[210px]'} ${align === 'right' ? 'ml-auto' : ''}`}
    >
      <div className="flex items-center gap-1.5 md:gap-2">
        <span className={`grid ${compact ? 'h-7 w-7' : 'h-8 w-8 md:h-9 md:w-9'} place-items-center rounded-[10px] md:rounded-[11px] shrink-0`} style={{ background: `${color}26`, color }}>
          <Icon className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        </span>
        <span className={`dis ${compact ? 'text-[11px]' : 'text-[12.5px] md:text-[14px]'} font-semibold t1 truncate`} style={{ fontFamily: "'Sora', sans-serif" }}>{title}</span>
      </div>
      <p className={`mt-1.5 md:mt-3 ${compact ? 'text-[9.5px] leading-[13px]' : 'text-[10.5px] md:text-[12px] leading-[15px] md:leading-[16px]'} t3 ${compact ? 'line-clamp-2' : ''}`}>{desc}</p>
      <div className={`mt-1.5 md:mt-3 flex items-center justify-between rounded-lg md:rounded-xl inset ${compact ? 'px-2 py-1' : 'px-2.5 md:px-3 py-1.5 md:py-2'}`}>
        <span className={`${compact ? 'text-[9px]' : 'text-[9.5px] md:text-[11px]'} t3 truncate`}>{metricLabel}</span>
        <span className={`${compact ? 'text-[10px]' : 'text-[11px] md:text-[13px]'} font-bold shrink-0`} style={{ color }}>{metricValue}</span>
      </div>
    </div>
  );
}

function Core({ small = false }) {
  const size = small ? 100 : 150;
  const ringOuter = small ? 150 : 250;
  const ringInner = small ? 122 : 200;
  return (
    <div className="relative grid place-items-center">
      <div className="absolute rounded-full border border-[#6D5EF7]/20 spin-slow" style={{ width: ringOuter, height: ringOuter }} />
      <div className="absolute rounded-full border border-dashed border-[#14B8A6]/30 spin-rev" style={{ width: ringInner, height: ringInner }} />
      <div className="absolute rounded-full aurora pulse-ring opacity-40" style={{ width: size + 20, height: size + 20 }} />
      <div className="absolute rounded-full" style={{ width: size + 50, height: size + 50, background: 'radial-gradient(circle,rgba(109,94,247,.32),transparent 68%)' }} />
      <div className="relative grid place-items-center rounded-full aurora breathe shadow-[0_24px_60px_rgba(109,94,247,.45)]" style={{ width: size, height: size }}>
        <div className="relative text-center text-white px-2">
          <div className={`slab ${small ? 'text-[18px]' : 'text-[22px] md:text-[26px]'} font-extrabold tracking-[-0.01em] leading-none`} style={{ fontFamily: "'Roboto Slab', serif" }}>numuv</div>
          <div className={`${small ? 'text-[8px]' : 'text-[9px] md:text-[10px]'} mt-1 uppercase tracking-[0.2em] text-white/85`}>AI Core</div>
        </div>
      </div>
    </div>
  );
}

function Hero({ onBookDemo }) {
  const navigate = useNavigate();
  const waLink = useWaLink();
  return (
    <section className="relative mx-auto max-w-[1240px] px-4 md:px-6 pt-12 md:pt-20 pb-4 md:pb-8" data-testid="hero-section">
      <div className="mx-auto max-w-[840px] text-center">
        <span className="inline-flex items-center gap-2 rounded-full chip px-4 py-1.5 text-[12px] md:text-[12.5px] font-medium tracking-[0.01em] t2 shadow-[0_4px_14px_rgba(17,19,42,.05)]" data-testid="hero-pill">
          <span className="h-1.5 w-1.5 rounded-full bg-[#14B8A6] shadow-[0_0_10px_#14B8A6]" />
          AI-native recruitment · not another job board
        </span>
        <h1 className="dis mt-6 md:mt-7 text-[38px] sm:text-[52px] md:text-[66px] font-extrabold leading-[1.05] tracking-[-0.035em] t1" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="hero-heading">
          Three journeys.<br />
          <span className="grad-text">One intelligent platform.</span>
        </h1>
        <p className="mx-auto mt-5 md:mt-6 max-w-[600px] text-[15px] md:text-[17.5px] leading-[24px] md:leading-[28px] t2">
          Applicants, staffing partners, and hiring teams converge at Numuv — where AI matching, autonomous interviews, and end-to-end management flow as one living system.
        </p>
        <div className="mt-7 md:mt-9 flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
          <button onClick={() => navigate('/login')} className="w-full sm:w-auto aurora-btn rounded-full px-6 md:px-7 py-3 md:py-3.5 text-[14.5px] md:text-[15px] font-semibold text-white shadow-[0_16px_40px_rgba(109,94,247,.38)] hover:brightness-105 hover:-translate-y-0.5 transition" data-testid="hero-primary-cta">
            Start hiring smarter
          </button>
          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer"
              className="w-full sm:w-auto rounded-full bg-[#25D366] px-6 md:px-7 py-3 md:py-3.5 text-[14.5px] md:text-[15px] font-semibold text-white shadow-[0_16px_40px_rgba(37,211,102,.32)] hover:brightness-105 hover:-translate-y-0.5 transition inline-flex items-center justify-center gap-2"
              data-testid="hero-whatsapp-cta">
              <WhatsAppIcon className="w-4 h-4" /> Start on WhatsApp
            </a>
          )}
          <button onClick={onBookDemo} className="w-full sm:w-auto rounded-full card thm px-6 md:px-7 py-3 md:py-3.5 text-[14.5px] md:text-[15px] font-semibold t1 soft hover:softlg transition inline-flex items-center justify-center gap-2" data-testid="hero-secondary-cta">
            <Play className="w-3 h-3 text-[#6D5EF7]" fill="currentColor" /> Book a demo
          </button>
        </div>
      </div>
      <DesktopConvergence />
      <MobileConvergence />
    </section>
  );
}

function Journeys() {
  const navigate = useNavigate();
  const items = [
    { role: 'applicant', to: '/for-applicants', icon: UserRound, color: '#12B6A6', title: 'For Applicants',
      desc: 'Build a rich profile once, then let AI surface roles that truly fit — with mock prep so you walk in ready.',
      bullets: ['Profile → instant auto-matching', 'AI interview with Jay, anytime', 'Mock interviews & feedback', 'Track selection → offer letter'] },
    { role: 'staffing', to: '/for-employers#staffing', icon: HeartHandshake, color: '#8B5CF6', title: 'For Staffing Orgs',
      desc: 'Onboard, nurture, and place candidates while managing every partnership from a single command center.',
      bullets: ['Candidate onboarding pipelines', 'Partnership management', 'Connection building at scale', 'Shared talent pools'] },
    { role: 'recruiter', to: '/for-employers#hiring', icon: Building2, color: '#4F7BF7', title: 'For Hiring Orgs',
      desc: 'Post roles, define AI screening questions, and track every candidate across your whole team hierarchy.',
      bullets: ['Job posting & AI question setup', 'Live candidate tracking', 'Team hierarchy management', 'Intelligent screening reports'] },
  ];
  return (
    <section id="platform" className="mx-auto max-w-[1240px] px-4 md:px-6 py-20 md:py-28" data-testid="journeys-section">
      <div className="mb-10 md:mb-16 text-center">
        <span className="text-[11.5px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">Three paths, one flow</span>
        <h2 className="dis mt-3 md:mt-4 text-[30px] md:text-[44px] font-bold tracking-[-0.03em] t1" style={{ fontFamily: "'Sora', sans-serif" }}>Built for everyone in the hire</h2>
        <p className="mx-auto mt-3 md:mt-4 max-w-[580px] text-[14.5px] md:text-[16.5px] leading-[24px] md:leading-[26px] t2">Each party gets a purpose-built journey — all orchestrated by the same intelligent engine.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-5 md:gap-7">
        {items.map((it) => (
          <article key={it.role} onClick={() => navigate(it.to)} data-testid={`journey-${it.role}`} className="card card-hover soft rounded-[22px] md:rounded-[26px] p-6 md:p-8 cursor-pointer">
            <span className="grid h-12 w-12 md:h-14 md:w-14 place-items-center rounded-[14px] md:rounded-[16px]" style={{ background: `${it.color}1F`, color: it.color }}>
              <it.icon className="w-5 h-5 md:w-6 md:h-6" />
            </span>
            <h3 className="dis mt-5 md:mt-6 text-[19px] md:text-[21px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{it.title}</h3>
            <p className="mt-2 md:mt-2.5 text-[13.5px] md:text-[14.5px] leading-[22px] md:leading-[23px] t2">{it.desc}</p>
            <ul className="mt-5 md:mt-6 space-y-2.5 md:space-y-3 text-[13.5px] md:text-[14px] t2">
              {it.bullets.map((b) => (
                <li key={b} className="flex items-center gap-3">
                  <Check className="w-4 h-4 shrink-0" style={{ color: it.color }} />
                  {b}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>
  );
}

function Pipeline() {
  const steps = [
    { icon: IdCard, color: '#12B6A6', title: 'Profile', desc: 'Rich, structured candidate data' },
    { icon: WandSparkles, color: '#8B5CF6', title: 'AI Matching', desc: 'Percentage-scored role fit' },
    { icon: Bot, color: 'aurora', title: 'AI Interview · Jay', desc: 'Video & audio, auto-analyzed' },
    { icon: UsersRound, color: '#4F7BF7', title: 'In-person', desc: 'Shortlist meets the team' },
    { icon: FilePen, color: '#10B981', title: 'Offer Letter', desc: 'Generated & sent instantly' },
  ];
  return (
    <section id="how-it-works" className="relative mx-auto max-w-[1240px] px-4 md:px-6 py-14 md:py-16" data-testid="pipeline-section">
      <div className="mb-10 md:mb-14 text-center">
        <span className="text-[11.5px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">Fully automated</span>
        <h2 className="dis mt-3 md:mt-4 text-[30px] md:text-[44px] font-bold tracking-[-0.03em] t1" style={{ fontFamily: "'Sora', sans-serif" }}>The candidate journey, on autopilot</h2>
        <p className="mx-auto mt-3 md:mt-4 max-w-[620px] text-[14.5px] md:text-[16.5px] leading-[24px] md:leading-[26px] t2">From first profile to signed offer — Numuv moves candidates through every stage without the busywork.</p>
      </div>
      <div className="relative rounded-[24px] md:rounded-[30px] card softlg p-6 md:p-12">
        <div className="relative">
          <div className="hidden md:block absolute left-[8%] right-[8%] top-[38px] h-[3px] overflow-hidden rounded-full inset">
            <div className="absolute inset-y-0 w-1/3 aurora rounded-full rail" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-5 md:gap-4">
            {steps.map((s) => {
              const isAurora = s.color === 'aurora';
              return (
                <div key={s.title} className="text-center">
                  <div className={`relative mx-auto grid h-[64px] w-[64px] md:h-[78px] md:w-[78px] place-items-center rounded-[18px] md:rounded-[22px] ${isAurora ? 'aurora text-white breathe' : 'card'}`}
                    style={!isAurora ? { borderColor: `${s.color}55`, color: s.color, boxShadow: `0 14px 34px ${s.color}2a` } : { boxShadow: '0 18px 40px rgba(109,94,247,.45)' }}>
                    <s.icon className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <p className="dis mt-3 md:mt-5 text-[13.5px] md:text-[15px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{s.title}</p>
                  <p className="mt-1 md:mt-1.5 text-[11.5px] md:text-[12.5px] t3">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function MeetJay() {
  return (
    <section id="jay" className="mx-auto grid max-w-[1240px] px-4 md:px-6 md:grid-cols-2 items-center gap-10 md:gap-20 py-20 md:py-28" data-testid="jay-section">
      <div>
        <span className="text-[11.5px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">Your AI interviewer</span>
        <h2 className="dis mt-3 md:mt-4 text-[32px] md:text-[46px] font-bold leading-[1.08] tracking-[-0.03em] t1" style={{ fontFamily: "'Sora', sans-serif" }}>
          Meet <span className="grad-text">Jay</span> — the interviewer who never sleeps
        </h2>
        <p className="mt-5 md:mt-6 text-[15px] md:text-[16.5px] leading-[25px] md:leading-[27px] t2">
          Jay conducts structured video and audio interviews on demand, then analyzes tone, content, and competency signals to produce an objective, bias-aware report for every candidate.
        </p>
        <div className="mt-7 md:mt-9 space-y-4 md:space-y-5">
          {[
            { icon: Waves, title: 'Video & audio recordings', desc: 'Every session captured and searchable.' },
            { icon: Brain, title: 'AI-based analysis', desc: 'Competency scoring & instant summaries.' },
            { icon: Dumbbell, title: 'Mock interview prep', desc: 'Applicants rehearse and get feedback.' },
          ].map((f) => (
            <div key={f.title} className="flex items-start gap-4">
              <span className="mt-0.5 grid h-10 w-10 md:h-11 md:w-11 shrink-0 place-items-center rounded-[13px] bg-[#6D5EF7]/12 text-[#6D5EF7]">
                <f.icon className="w-4 h-4 md:w-5 md:h-5" />
              </span>
              <div>
                <p className="dis text-[14.5px] md:text-[15.5px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{f.title}</p>
                <p className="mt-0.5 text-[13.5px] md:text-[14px] t3">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative">
        <div className="absolute -inset-6 md:-inset-8 rounded-[40px]" style={{ background: 'radial-gradient(circle at 60% 40%,rgba(109,94,247,.22),transparent 70%)' }} />
        <div className="relative card softlg rounded-[24px] md:rounded-[28px] p-5 md:p-6 float">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-[#F43F5E]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#F59E0B]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#10B981]" />
            </div>
            <span className="flex items-center gap-1.5 text-[11px] md:text-[11.5px] font-medium text-[#12B6A6]">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#12B6A6]" /> REC · 12:04
            </span>
          </div>
          <div className="relative mt-4 grid h-[200px] md:h-[236px] place-items-center overflow-hidden rounded-[18px] md:rounded-[20px] jayscreen">
            <div className="absolute inset-0 grid-bg opacity-50" />
            <div className="relative grid place-items-center">
              <div className="absolute h-[120px] w-[120px] md:h-[140px] md:w-[140px] rounded-full border border-[#6D5EF7]/30 spin-slow" />
              <div className="absolute h-[130px] w-[130px] md:h-[150px] md:w-[150px] rounded-full aurora pulse-ring opacity-40" />
              <div className="grid h-[76px] w-[76px] md:h-[92px] md:w-[92px] place-items-center rounded-full aurora text-white breathe shadow-[0_18px_44px_rgba(109,94,247,.5)]">
                <Bot className="w-7 h-7 md:w-8 md:h-8" />
              </div>
            </div>
            <div className="absolute bottom-3 left-3 flex items-end gap-1 rounded-full glass px-3 py-2 shadow-sm">
              {[
                { c: '#6D5EF7', h: 16, d: 0 }, { c: '#8B5CF6', h: 9, d: 0.2 },
                { c: '#14B8A6', h: 18, d: 0.4 }, { c: '#6D5EF7', h: 12, d: 0.1 },
                { c: '#5B8DEF', h: 15, d: 0.3 },
              ].map((b, i) => (
                <span key={i} className="w-1 rounded-full bar" style={{ background: b.c, height: b.h, ['--h']: `${b.h}px`, animationDelay: `${b.d}s` }} />
              ))}
            </div>
            <div className="absolute bottom-3 right-3 grid h-12 w-16 md:h-14 md:w-20 place-items-center overflow-hidden rounded-xl card t4 shadow-sm">
              <UserRound className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-4 rounded-[14px] md:rounded-[16px] inset p-3.5 md:p-4">
            <p className="text-[10.5px] md:text-[11px] font-semibold uppercase tracking-[0.14em] t3">Jay asks</p>
            <p className="mt-1.5 text-[13.5px] md:text-[14px] leading-[20px] md:leading-[21px] t2">
              &ldquo;Walk me through a project where you owned the outcome end-to-end.&rdquo;
            </p>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {[
              { v: '92%', label: 'Communication' },
              { v: '88%', label: 'Technical fit' },
              { v: 'A+', label: 'Overall' },
            ].map((s) => (
              <div key={s.label} className="rounded-[14px] md:rounded-[16px] inset p-3 md:p-3.5 text-center">
                <p className="dis grad-text text-[20px] md:text-[22px] font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>{s.v}</p>
                <p className="mt-0.5 text-[10px] md:text-[10.5px] t3">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const items = [
    { id: 'ai-matching', icon: WandSparkles, color: '#6D5EF7', title: 'AI-powered matching', desc: 'Not a job board — a matching engine that scores fit with real percentages, not keywords.' },
    { id: 'screening', icon: SearchCheck, color: '#12B6A6', title: 'Intelligent screening', desc: 'Automated screening surfaces the strongest candidates and flags gaps before you spend a minute.' },
    { id: 'reporting', icon: ChartPie, color: '#8B5CF6', title: 'Reporting & analytics', desc: 'Clear, exportable reports on every candidate, interview, and pipeline stage.' },
    { id: 'hierarchy', icon: Network, color: '#4F7BF7', title: 'Team hierarchy', desc: 'Model your whole org — roles, permissions, and approval chains that match reality.' },
    { id: 'offers', icon: FilePen, color: '#10B981', title: 'Automated offers', desc: 'Generate, route, and track offer letters the moment a candidate is selected.' },
    { id: 'recordings', icon: Video, color: '#F59E0B', title: 'Recorded interviews', desc: 'Every video and audio session stored, transcribed, and analyzed automatically.' },
  ];
  return (
    <section id="features" className="mx-auto max-w-[1240px] px-4 md:px-6 py-14 md:py-16" data-testid="features-section">
      <div className="mb-10 md:mb-14 text-center">
        <span className="text-[11.5px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">One platform</span>
        <h2 className="dis mt-3 md:mt-4 text-[30px] md:text-[44px] font-bold tracking-[-0.03em] t1" style={{ fontFamily: "'Sora', sans-serif" }}>End-to-end recruitment intelligence</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
        {items.map((it) => (
          <article id={it.id} key={it.title} className="card card-hover soft rounded-[20px] md:rounded-[22px] p-6 md:p-7" data-testid={`feature-${it.id}`}>
            <span className="grid h-11 w-11 md:h-12 md:w-12 place-items-center rounded-[13px] md:rounded-[14px]" style={{ background: `${it.color}1F`, color: it.color }}>
              <it.icon className="w-5 h-5" />
            </span>
            <h3 className="dis mt-4 md:mt-5 text-[16.5px] md:text-[18px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{it.title}</h3>
            <p className="mt-2 text-[13.5px] md:text-[14px] leading-[21px] md:leading-[22px] t3">{it.desc}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function Stats() {
  const items = [
    { v: '73%', label: 'Faster time-to-hire' },
    { v: '3', label: 'Journeys unified' },
    { v: '24/7', label: 'AI interviews with Jay' },
    { v: '94%', label: 'Avg. match accuracy' },
  ];
  return (
    <section className="mx-auto max-w-[1240px] px-4 md:px-6 py-14 md:py-16" data-testid="stats-section">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 rounded-[24px] md:rounded-[30px] card softlg p-8 md:p-12">
        {items.map((s) => (
          <div key={s.label} className="text-center">
            <p className="dis grad-text text-[38px] md:text-[50px] font-extrabold tracking-[-0.03em]" style={{ fontFamily: "'Sora', sans-serif" }}>{s.v}</p>
            <p className="mt-1 text-[12.5px] md:text-[13.5px] t3">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function CTA({ onBookDemo }) {
  const navigate = useNavigate();
  return (
    <section className="mx-auto max-w-[1240px] px-4 md:px-6 py-16 md:py-24" data-testid="cta-section">
      <div className="relative overflow-hidden rounded-[28px] md:rounded-[36px] p-10 md:p-20 text-center softlg aurora">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-[300px] md:h-[360px] w-[600px] md:w-[680px] -translate-x-1/2 rounded-full anim-drift"
          style={{ background: 'radial-gradient(circle,rgba(255,255,255,.35),transparent 65%)' }} />
        <div className="pointer-events-none absolute inset-0 grid-bg opacity-15" />
        <div className="relative">
          <h2 className="dis mx-auto max-w-[680px] text-[32px] md:text-[50px] font-bold leading-[1.1] tracking-[-0.03em] text-white" style={{ fontFamily: "'Sora', sans-serif" }}>
            Where every hiring journey converges
          </h2>
          <p className="mx-auto mt-4 md:mt-5 max-w-[540px] text-[15px] md:text-[17px] leading-[24px] md:leading-[27px] text-white/85">
            Join the platform reimagining recruitment as one intelligent, living system.
          </p>
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <button onClick={() => navigate('/login')} className="w-full sm:w-auto rounded-full bg-white px-6 md:px-8 py-3.5 md:py-4 text-[14.5px] md:text-[15px] font-semibold text-[#6D5EF7] shadow-[0_16px_40px_rgba(17,19,42,.22)] hover:-translate-y-0.5 transition" data-testid="cta-primary">
              Get started free
            </button>
            <button onClick={onBookDemo} className="w-full sm:w-auto rounded-full border border-white/50 bg-white/10 px-6 md:px-8 py-3.5 md:py-4 text-[14.5px] md:text-[15px] font-semibold text-white backdrop-blur hover:bg-white/20 transition" data-testid="cta-secondary">
              Book a demo
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatsAppFab() {
  const waLink = useWaLink();
  if (!waLink) return null;
  return (
    <a href={waLink} target="_blank" rel="noopener noreferrer" aria-label="Chat with Numuv on WhatsApp"
      className="fixed bottom-6 right-6 z-40 grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-white shadow-[0_12px_32px_rgba(37,211,102,.45)] hover:scale-105 hover:brightness-105 transition-transform"
      data-testid="homepage-whatsapp-fab">
      <WhatsAppIcon className="w-7 h-7" />
    </a>
  );
}

const HOMEPAGE_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${SITE}/#page`,
      url: `${SITE}/`,
      name: 'Numuv — AI-native recruitment platform',
      description: 'Numuv unifies applicants, staffing partners and hiring teams on one AI-native recruitment platform with vector matching, autonomous AI interviews by Jay and end-to-end pipeline automation.',
      inLanguage: 'en',
      isPartOf: { '@id': `${SITE}#website` },
      about: { '@id': `${SITE}#software` },
    },
    {
      '@type': 'ItemList',
      name: 'Who Numuv is built for',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Applicants', url: `${SITE}/for-applicants` },
        { '@type': 'ListItem', position: 2, name: 'Hiring & staffing teams', url: `${SITE}/for-employers` },
        { '@type': 'ListItem', position: 3, name: 'Jay, the AI interviewer', url: `${SITE}/meet-jay` },
      ],
    },
  ],
};

export default function HomePage() {
  return (
    <MarketingLayout>
      {({ onBookDemo }) => (
        <>
          <Seo
            title="AI recruitment platform for applicants, staffing & hiring teams"
            description="Numuv is the AI-native recruitment platform. Vector-powered matching, autonomous AI interviews by Jay, and end-to-end hiring workflows for applicants, staffing organisations and hiring teams."
            path="/"
            keywords="AI recruitment, applicant tracking, staffing platform, AI interview, resume screening, hiring software, recruitment automation, talent matching"
            jsonLd={HOMEPAGE_JSONLD}
          />
          <Hero onBookDemo={onBookDemo} />
          <Journeys />
          <Pipeline />
          <MeetJay />
          <Features />
          <Stats />
          <CTA onBookDemo={onBookDemo} />
          <WhatsAppFab />
          <Toaster position="top-center" richColors />
        </>
      )}
    </MarketingLayout>
  );
}
